import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield,
  Users,
  Search,
  Settings,
  Check,
  X,
  Loader2,
  Crown,
  Sparkles,
  Building2,
  User,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Feature flag display names and descriptions
const FLAG_CONFIG = {
  proposals: { label: "Program Proposals", description: "Create and manage program proposals" },
  events: { label: "Events", description: "Track participation and noteworthy events" },
  inventory: { label: "Inventory", description: "QR-coded supply inventory management" },
  books: { label: "Books", description: "Book catalog management" },
  book_displays: { label: "Book Displays", description: "Manage book displays" },
  literacy_stations: { label: "Literacy Stations", description: "Track literacy stations" },
  yearly_goals: { label: "Yearly Goals", description: "Set and track yearly goals" },
  shout_outs: { label: "Shout Outs", description: "Staff recognition and shout outs" },
  ai_assistant: { label: "AI Assistant", description: "AI-powered assistance and reports" },
  supply_inventory: { label: "Supply Inventory", description: "Budget and supply overage tracking" },
  supervisor_dashboard: { label: "Supervisor Dashboard", description: "Staff oversight and reports" },
  user_management: { label: "User Management", description: "Manage user accounts" }
};

const TIER_CONFIG = {
  free: { label: "Free", icon: User, color: "text-gray-500", bg: "bg-gray-100" },
  basic: { label: "Basic", icon: Sparkles, color: "text-blue-600", bg: "bg-blue-100" },
  pro: { label: "Pro", icon: Crown, color: "text-purple-600", bg: "bg-purple-100" },
  enterprise: { label: "Enterprise", icon: Building2, color: "text-amber-600", bg: "bg-amber-100" }
};

export const FeatureFlagsAdmin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tierPresets, setTierPresets] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});

  useEffect(() => {
    fetchUsers();
    fetchTierPresets();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchTierPresets = async () => {
    try {
      const response = await axios.get(`${API}/admin/tier-presets`);
      setTierPresets(response.data);
    } catch (error) {
      console.error("Failed to fetch tier presets");
    }
  };

  const handleToggleFlag = async (userId, flagName) => {
    try {
      const response = await axios.post(`${API}/admin/users/${userId}/toggle-flag/${flagName}`);
      toast.success(`${FLAG_CONFIG[flagName]?.label || flagName} toggled`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to toggle flag");
    }
  };

  const handleApplyTierPreset = async (userId, tier) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/apply-tier-preset?tier=${tier}`);
      toast.success(`Applied ${TIER_CONFIG[tier]?.label || tier} preset`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to apply preset");
    }
  };

  const handleSaveFlags = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/feature-flags`, {
        feature_flags: selectedUser.feature_flags
      });
      toast.success("Feature flags saved");
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to save flags");
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleExpanded = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const countEnabledFlags = (flags) => {
    if (!flags) return 0;
    return Object.values(flags).filter(v => v === true).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading flex items-center gap-2">
            <Shield className="h-8 w-8 text-purple-600" />
            Feature Flags Admin
          </h1>
          <p className="text-muted-foreground">Manage subscription tiers and module access per user</p>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="user-search"
              />
            </div>
            <Badge variant="outline" className="text-sm">
              {filteredUsers.length} users
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <div className="space-y-3">
        {filteredUsers.map((user) => {
          const TierIcon = TIER_CONFIG[user.subscription_tier]?.icon || User;
          const tierConfig = TIER_CONFIG[user.subscription_tier] || TIER_CONFIG.free;
          const enabledCount = countEnabledFlags(user.feature_flags);
          const totalFlags = Object.keys(FLAG_CONFIG).length;
          
          return (
            <Card key={user.id} className="overflow-hidden" data-testid={`user-card-${user.id}`}>
              <Collapsible open={expandedUsers[user.id]} onOpenChange={() => toggleExpanded(user.id)}>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${tierConfig.bg}`}>
                        <TierIcon className={`h-5 w-5 ${tierConfig.color}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{user.name}</h3>
                          {user.is_admin && (
                            <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                              Admin
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Badge className={`${tierConfig.bg} ${tierConfig.color} border-0`}>
                        {tierConfig.label}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {enabledCount}/{totalFlags} enabled
                      </Badge>
                      
                      {/* Quick Tier Selector */}
                      <Select
                        value={user.subscription_tier || "free"}
                        onValueChange={(tier) => handleApplyTierPreset(user.id, tier)}
                      >
                        <SelectTrigger className="w-32" data-testid={`tier-select-${user.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(TIER_CONFIG).map(([tier, config]) => (
                            <SelectItem key={tier} value={tier}>
                              <div className="flex items-center gap-2">
                                <config.icon className={`h-4 w-4 ${config.color}`} />
                                {config.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm">
                          {expandedUsers[user.id] ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                  </div>
                </div>
                
                <CollapsibleContent>
                  <div className="px-4 pb-4 border-t pt-4 bg-muted/20">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {Object.entries(FLAG_CONFIG).map(([flag, config]) => {
                        const isEnabled = user.feature_flags?.[flag] || false;
                        return (
                          <div
                            key={flag}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              isEnabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{config.label}</p>
                            </div>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() => handleToggleFlag(user.id, flag)}
                              data-testid={`flag-toggle-${user.id}-${flag}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {filteredUsers.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">No users found</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Feature Flags</DialogTitle>
            <DialogDescription>
              Configure module access for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="grid grid-cols-2 gap-4 py-4">
              {Object.entries(FLAG_CONFIG).map(([flag, config]) => (
                <div key={flag} className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <Label className="font-medium">{config.label}</Label>
                    <p className="text-xs text-muted-foreground">{config.description}</p>
                  </div>
                  <Switch
                    checked={selectedUser.feature_flags?.[flag] || false}
                    onCheckedChange={(checked) => {
                      setSelectedUser({
                        ...selectedUser,
                        feature_flags: {
                          ...selectedUser.feature_flags,
                          [flag]: checked
                        }
                      });
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveFlags} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeatureFlagsAdmin;
